import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { DatasourcesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragment';
import { DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace';
import {
  TableDownstreamPublishedDatasourcesFilteredByColumnQuery,
  TableDownstreamPublishedDatasourcesFilteredByColumnQueryVariables
} from '../../../gen-src/queries/TableDownstreamPublishedDatasourcesFilteredByColumnQuery';
import {
  TableDownstreamPublishedDatasourcesFilteredByColumnWithoutPersonalSpaceQuery,
  TableDownstreamPublishedDatasourcesFilteredByColumnWithoutPersonalSpaceQueryVariables
} from '../../../gen-src/queries/TableDownstreamPublishedDatasourcesFilteredByColumnWithoutPersonalSpaceQuery';
import {
  TableDownstreamPublishedDatasourcesQuery,
  TableDownstreamPublishedDatasourcesQueryVariables,
} from '../../../gen-src/queries/TableDownstreamPublishedDatasourcesQuery';
import {
  TableDownstreamPublishedDatasourcesWithoutPersonalSpaceQuery,
  TableDownstreamPublishedDatasourcesWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/TableDownstreamPublishedDatasourcesWithoutPersonalSpaceQuery';
import { PublishedDatasourceSortOrder } from '../../../gen-src/queries/globalTypes';
import { TableDownstreamPublishedDatasourcesFilteredByColumnQueryGql } from '../../queries/TableDownstreamPublishedDatasourcesFilteredByColumnQueryGql';
import { TableDownstreamPublishedDatasourcesFilteredByColumnWithoutPersonalSpaceQueryGql } from '../../queries/TableDownstreamPublishedDatasourcesFilteredByColumnWithoutPersonalSpaceQueryGql';
import { TableDownstreamPublishedDatasourcesQueryGql } from '../../queries/TableDownstreamPublishedDatasourcesQueryGql';
import { TableDownstreamPublishedDatasourcesWithoutPersonalSpaceQueryGql } from '../../queries/TableDownstreamPublishedDatasourcesWithoutPersonalSpaceQueryGql';
import {
  DatasourcesDisplayGrid,
  DatasourcesDisplayGridRowData,
  getRowDataForDatasource,
  getRowDataForDatasourceWithoutPersonalSpace
} from '../sharedDisplayGrid/DatasourcesDisplayGrid';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

type QueryVariables = TableDownstreamPublishedDatasourcesQueryVariables;
type QueryResults = TableDownstreamPublishedDatasourcesQuery;
type QueryResultListNode = DatasourcesDisplayGridRowDataFragment;
type GridRowData = DatasourcesDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = TableDownstreamPublishedDatasourcesWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = TableDownstreamPublishedDatasourcesWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = TableDownstreamPublishedDatasourcesFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableDownstreamPublishedDatasourcesFilteredByColumnQuery;

type QueryVariablesWithFilterWithoutPersonalSpace =
  TableDownstreamPublishedDatasourcesFilteredByColumnWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace =
  TableDownstreamPublishedDatasourcesFilteredByColumnWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamDatasourcesConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.columns && resultData.columns[0] &&
    resultData.columns[0].downstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.columns && resultData.columns[0] &&
    resultData.columns[0].downstreamDatasourcesConnection;
}

export type TableDownstreamDatasourcesQueryGridProps =
  GridSortProps<DatasourcesValidSortFields> & {
    readonly tableId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function TableDownstreamDatasourcesQueryGrid(props: TableDownstreamDatasourcesQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamDatasourcesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDatasourcesLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={TableDownstreamPublishedDatasourcesFilteredByColumnQueryGql}
        partialQueryVariables={{ columnId: props.filteredColumnId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={TableDownstreamPublishedDatasourcesQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={TableDownstreamPublishedDatasourcesFilteredByColumnWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ columnId: props.filteredColumnId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={TableDownstreamPublishedDatasourcesWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  );
}
